import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FolderKanban, Plus, X } from "lucide-react";
import { useEffect, useState, type SyntheticEvent } from "react";

import { PageHeader } from "../../components/shell/AppShell";
import { Button, IconButton, TextField } from "../../components/ui/Controls";
import {
  AlertBanner,
  EmptyState,
  ErrorState,
  InlineResult,
  Skeleton,
} from "../../components/ui/Feedback";
import { CodeValue } from "../../components/ui/ResponsiveData";
import { apiErrorMessage, useAuth } from "../../lib/auth";
import { projectsApi } from "../../lib/api";
import { formatDateTime } from "../../lib/format";
import { ProjectWorkspace } from "./ProjectWorkspace";
import { ProjectWizard } from "./wizard/ProjectWizard";

export function ProjectsPage() {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const canManage = auth.identity?.permissions.includes("projects:manage") === true;
  const [selectedProjectId, setSelectedProjectId] = useState<string>();
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string>();
  const [createdRequestId, setCreatedRequestId] = useState<string>();
  const projects = useQuery({
    queryKey: ["projects"],
    queryFn: ({ signal }) => projectsApi.list(signal),
    retry: false,
  });

  useEffect(() => {
    const items = projects.data?.items;
    if (items === undefined || items.length === 0) return;
    if (
      selectedProjectId === undefined ||
      !items.some((project) => project.id === selectedProjectId)
    ) {
      setSelectedProjectId(items[0]?.id);
    }
  }, [projects.data?.items, selectedProjectId]);

  const detail = useQuery({
    queryKey: ["project", selectedProjectId],
    queryFn: ({ signal }) => projectsApi.detail(selectedProjectId ?? "", signal),
    enabled: selectedProjectId !== undefined,
    retry: false,
  });

  const closeCreate = () => {
    setCreateOpen(false);
    setName("");
    setSlug("");
    setCreateError(undefined);
  };

  const create = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canManage) return;
    setCreateBusy(true);
    setCreateError(undefined);
    setCreatedRequestId(undefined);
    try {
      const response = await projectsApi.create({
        name: name.trim(),
        ...(slug.trim() === "" ? {} : { slug: slug.trim() }),
      });
      await queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.setQueryData(["project", response.project.id], response);
      setSelectedProjectId(response.project.id);
      setCreatedRequestId(response.requestId);
      closeCreate();
    } catch (caught) {
      setCreateError(apiErrorMessage(caught));
    } finally {
      setCreateBusy(false);
    }
  };

  const openCreateButton = canManage ? (
    <Button
      onClick={() => {
        setCreateOpen(true);
        setCreatedRequestId(undefined);
      }}
    >
      <Plus aria-hidden="true" size={18} /> Proje oluştur
    </Button>
  ) : undefined;

  return (
    <div className="page">
      <PageHeader
        action={openCreateButton}
        description="Git veya ZIP kaynağını değişmez release'e dönüştürün; planı inceleyin ve işlemleri canlı takip edin."
        title="Projeler"
      />

      {createdRequestId === undefined ? null : (
        <InlineResult requestId={createdRequestId}>
          Proje ve production ortamı atomik olarak oluşturuldu.
        </InlineResult>
      )}

      {canManage ? <ProjectWizard /> : null}

      {createOpen ? (
        <section aria-labelledby="create-project-heading" className="card stack">
          <div className="split-row">
            <div>
              <p className="eyebrow">Yeni uygulama</p>
              <h2 id="create-project-heading">Proje oluştur</h2>
            </div>
            <IconButton
              icon={<X aria-hidden="true" size={20} />}
              label="Proje formunu kapat"
              onClick={closeCreate}
            />
          </div>
          <form
            className="form-stack"
            onSubmit={(event) => {
              void create(event);
            }}
          >
            <TextField
              autoComplete="organization-title"
              label="Proje adı"
              maxLength={120}
              minLength={1}
              onChange={(event) => {
                setName(event.currentTarget.value);
              }}
              required
              value={name}
            />
            <TextField
              help="Boş bırakırsanız sunucu proje adından güvenli bir slug üretir. Çakışmalar sessizce yeniden adlandırılmaz."
              label="Slug (isteğe bağlı)"
              maxLength={63}
              onChange={(event) => {
                setSlug(event.currentTarget.value.toLowerCase());
              }}
              pattern="[a-z0-9][a-z0-9-]{0,62}"
              placeholder="ornek-uygulama"
              value={slug}
            />
            {createError === undefined ? null : (
              <AlertBanner heading="Proje oluşturulamadı" tone="error">
                <p>{createError}</p>
              </AlertBanner>
            )}
            <div className="form-actions">
              <Button busy={createBusy} busyLabel="Proje oluşturuluyor…" type="submit">
                Proje ve production ortamı oluştur
              </Button>
              <Button onClick={closeCreate} variant="secondary">
                Vazgeç
              </Button>
            </div>
          </form>
        </section>
      ) : null}

      {projects.isLoading ? <Skeleton label="Projeler yükleniyor" /> : null}
      {projects.isError ? (
        <ErrorState
          onRetry={() => {
            void projects.refetch();
          }}
        />
      ) : null}
      {projects.data?.items.length === 0 ? (
        <EmptyState
          action={openCreateButton}
          body={
            canManage
              ? "İlk projenizi oluşturduğunuzda production ortamı ve güvenli yapılandırma sürümü birlikte hazırlanır."
              : "Henüz görünür bir proje yok. Proje yöneticinizden erişim isteyin."
          }
          heading="Henüz proje yok"
        />
      ) : null}

      {(projects.data?.items.length ?? 0) > 0 ? (
        <section aria-labelledby="project-selection-heading" className="card stack">
          <div>
            <p className="eyebrow">Çalışma alanı</p>
            <h2 id="project-selection-heading">Proje seçin</h2>
          </div>
          <div className="source-method-grid">
            {projects.data?.items.map((project) => (
              <button
                aria-current={project.id === selectedProjectId ? "true" : undefined}
                className="source-method"
                key={project.id}
                onClick={() => {
                  setSelectedProjectId(project.id);
                }}
                type="button"
              >
                <FolderKanban aria-hidden="true" size={24} strokeWidth={1.6} />
                <strong>{project.name}</strong>
                <span>{project.slug}</span>
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {detail.isLoading ? <Skeleton label="Proje çalışma alanı yükleniyor" /> : null}
      {detail.isError ? (
        <ErrorState
          onRetry={() => {
            void detail.refetch();
          }}
        />
      ) : null}
      {detail.data === undefined ? null : (
        <>
          <section aria-labelledby="project-detail-heading" className="card stack">
            <div className="split-row">
              <div>
                <p className="eyebrow">{detail.data.project.slug}</p>
                <h2 id="project-detail-heading">{detail.data.project.name}</h2>
              </div>
              <span className="technical">rev {detail.data.project.revision}</span>
            </div>
            <dl className="definition-grid">
              <div>
                <dt>Proje kimliği</dt>
                <dd>
                  <CodeValue>{detail.data.project.id}</CodeValue>
                </dd>
              </div>
              <div>
                <dt>Production ortamı</dt>
                <dd>{detail.data.productionEnvironment.name}</dd>
              </div>
              <div>
                <dt>İstenen nesil</dt>
                <dd>{detail.data.productionEnvironment.desiredGeneration}</dd>
              </div>
              <div>
                <dt>Yapılandırma sürümü</dt>
                <dd>{detail.data.currentConfigVersion}</dd>
              </div>
              <div>
                <dt>Oluşturuldu</dt>
                <dd>{formatDateTime(detail.data.project.createdAt)}</dd>
              </div>
            </dl>
          </section>
          <ProjectWorkspace key={detail.data.project.id} projectId={detail.data.project.id} />
        </>
      )}
    </div>
  );
}
